import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    Form,
    Button,
    Alert,
    Spinner,
} from "react-bootstrap";
import { Input } from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader";

import { numberTranslationForm } from "../../configs/Forms/AdvancedForms";
import {
    getNumberTranslation,
    insertNumberTranslation,
    updateNumberTranslation,
} from "./numTranslationService";

const titleCase = (s) =>
    s.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const NumTranslationAddEdit = () => {
    const { number_translation_uuid } = useParams();
    const navigate = useNavigate();
    const editing = Boolean(number_translation_uuid);

    // ----------------------------
    // MAIN STATE
    // ----------------------------
    const [translation, setTranslation] = useState(() => {
        if (editing) return {};

        const defaults = {};
        Object.entries(numberTranslationForm.sections).forEach(([section, fields]) =>
            fields.forEach((f) => {
                defaults[f] = numberTranslationForm.defaults?.[f] ?? "";
            })
        );
        return defaults;
    });

    const [details, setDetails] = useState([]);

    const [loading, setLoading] = useState(editing);
    const [error, setError] = useState("");

    const setterMap = { Details: setDetails };
    const arrayMap = { Details: details };

    // ----------------------------
    // FETCH EXISTING
    // ----------------------------
    useEffect(() => {
        if (!editing) return;

        const fetchTranslation = async () => {
            try {
                setLoading(true);
                const data = await getNumberTranslation(number_translation_uuid);

                if (data.error) {
                    setError(data.error);
                    return;
                }

                // Main info
                setTranslation({
                    number_translation_uuid: data.number_translation_uuid,
                    number_translation_name: data.number_translation_name,
                    number_translation_enabled: data.number_translation_enabled,
                    number_translation_description: data.number_translation_description || "",
                    number_translation_regex: data.number_translation_regex,
                    number_translation_replace: data.number_translation_replace,
                    number_translation_order: data.number_translation_order,
                });

                // Details array (if any)
                setDetails(data.details || []);
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchTranslation();
    }, [number_translation_uuid, editing]);

    // ----------------------------
    // HELPERS
    // ----------------------------
    const handleChange = (field, value) =>
        setTranslation((prev) => ({ ...prev, [field]: value }));

    const handleArrayChange = (setter, index, field, value, array) => {
        const updated = [...array];
        updated[index] = { ...updated[index], [field]: value };
        setter(updated);
    };

    const handleAddItem = (setter, sectionName, array) => {
        const template = {};
        numberTranslationForm.sections[sectionName].forEach((f) => {
            template[f] = numberTranslationForm.defaults?.[f] ?? "";
        });
        setter([...array, template]);
    };

    const handleDeleteItem = (sectionName, idx) => {
        const setter = setterMap[sectionName];
        const array = arrayMap[sectionName];
        setter(array.filter((_, i) => i !== idx));
    };

    const handleSave = async () => {
        setError("");
        const missing = [];

        // Validate main required fields
        numberTranslationForm.required.forEach((f) => {
            if (!translation[f]) missing.push(f);
        });

        // Validate Details required fields
        Object.entries(numberTranslationForm.requiredSections || {}).forEach(
            ([section, fields]) => {
                const items = { Details: details }[section] || [];
                items.forEach((item, idx) => {
                    fields.forEach((f) => {
                        if (!item[f]) missing.push(`${section}[${idx}].${f}`);
                    });
                });
            }
        );

        if (missing.length) {
            setError(`Required fields missing: ${missing.join(", ")}`);
            return;
        }

        const user = JSON.parse(localStorage.getItem("user"));
        const user_uuid = user?.user_uuid || null;
        const site = JSON.parse(localStorage.getItem("selectedSite"));
        const site_uuid = site?.site_uuid;

        try {
            const sanitizedMain = {};
            Object.entries(translation).forEach(([k, v]) => {
                if (v !== "" && v !== null && v !== undefined) sanitizedMain[k] = v;
            });

            const sanitizeArray = (arr) =>
                arr
                    .map((item) => {
                        const sanitized = {};
                        Object.entries(item).forEach(([k, v]) => {
                            if (v !== "" && v !== null && v !== undefined) sanitized[k] = v;
                        });
                        return sanitized;
                    })
                    .filter((obj) => Object.keys(obj).length > 0);

            const payload = {
                translation: sanitizedMain,
                details: sanitizeArray(details),
                site_uuid,
                user_uuid,
            };

            if (editing) {
                await updateNumberTranslation(payload.translation, payload.details, payload.site_uuid, payload.user_uuid);
            } else {
                await insertNumberTranslation(payload.translation, payload.details, payload.site_uuid, payload.user_uuid);
            }

            navigate("/admin/hucs/numberTranslations");
        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to save");
        }
    };

    const renderField = (field, value, onChange) => {
        const type = numberTranslationForm.types[field];
        const options = numberTranslationForm.options?.[field] || [];
        const defaultValue = numberTranslationForm.defaults?.[field];
        const effectiveValue = value ?? defaultValue ?? "";
        const label =
            numberTranslationForm.labels?.[field] || titleCase(field);

        const placeholder = value
            ? ""
            : `Enter ${label.toLowerCase()}`;

        // BOOLEAN
        if (type === "boolean") {
            return (
                <Input
                    type="select"
                    value={effectiveValue}
                    onChange={(e) => onChange(e.target.value)}
                >
                    <option value="true">True</option>
                    <option value="false">False</option>
                </Input>
            );
        }

        // SELECT
        if (type === "select") {
            return (
                <Input
                    type="select"
                    value={effectiveValue}
                    onChange={(e) => onChange(e.target.value)}
                >
                    <option value="">
                        Select {numberTranslationForm.labels[field]}
                    </option>

                    {options.map((opt) =>
                        typeof opt === "object" ? (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ) : (
                            <option key={opt} value={opt}>
                                {opt}
                            </option>
                        )
                    )}
                </Input>
            );
        }

        // DEFAULT (text / number)
        return (
            <Input
                type="text"
                value={effectiveValue}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
            />
        );
    };

    // ----------------------------
    // LOADING SCREEN
    // ----------------------------
    if (loading)
        return (
            <div className="text-center mt-5">
                <Spinner animation="border" />
                <p className="mt-2">Loading Number Translation...</p>
            </div>
        );

    // ----------------------------
    // RENDER
    // ----------------------------
    return (
        <>
            <SimpleHeader
                name={`${editing ? "Edit" : "Add"} Number Translation`}
                parentName="Number Translations"
            />

            <Container className="mt--6">
                <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
                    <Card.Header className="header-theme">
                        <h4 className="mb-0">{editing ? "EDIT NUMBER TRANSLATION" : "ADD NUMBER TRANSLATION"}</h4>
                    </Card.Header>

                    <Card.Body className="p-4">
                        {error && <Alert variant="danger">{error}</Alert>}

                        <Form>
                            {Object.entries(numberTranslationForm.sections).map(([sectionName, fields]) => {
                                const setter = setterMap[sectionName];
                                const items = arrayMap[sectionName];

                                return (
                                    <div key={sectionName}>
                                        <h5 className="section-title mb-3 mt-4" style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                                            {sectionName}
                                        </h5>

                                        {/* ARRAY SECTION (Details) */}
                                        {sectionName === "Details" &&
                                            items?.map((item, idx) => (
                                                <Row key={idx} className="mb-3">
                                                    {fields.map((field, i) => (
                                                        <Col md={4} key={i}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>
                                                                    {numberTranslationForm.labels[field] || field}
                                                                    {(numberTranslationForm.requiredSections?.[sectionName] || []).includes(field) && (
                                                                        <span style={{ color: "red" }}> *</span>
                                                                    )}
                                                                </Form.Label>

                                                                {renderField(field, item[field], (val) =>
                                                                    handleArrayChange(setter, idx, field, val, items)
                                                                )
                                                                }
                                                            </Form.Group>
                                                        </Col>
                                                    ))}

                                                    <Col md={2}>
                                                        <Button variant="danger" onClick={() => handleDeleteItem(sectionName, idx)}>
                                                            Delete
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            ))}

                                        {sectionName === "Details" && (
                                            <Button className="mt-2" onClick={() => handleAddItem(setter, sectionName, items)}>
                                                Add Detail
                                            </Button>
                                        )}

                                        {/* BASIC INFO */}
                                        {sectionName === "Basic Info" && (
                                            <Row className="mb-2">
                                                {fields.map((field, i) => (
                                                    <Col md={4} key={i}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>
                                                                {numberTranslationForm.labels[field] || field}
                                                                {numberTranslationForm.required.includes(field) && <span style={{ color: "red" }}> *</span>}
                                                            </Form.Label>
                                                            {renderField(field, translation[field], (value) =>
                                                                handleChange(field, value)
                                                            )}
                                                        </Form.Group>
                                                    </Col>
                                                ))}
                                            </Row>
                                        )}
                                    </div>
                                );
                            })}

                            <div className="mt-4 d-flex justify-content-between">
                                <Button variant="danger" onClick={() => navigate("/admin/hucs/number-translations")}>
                                    Cancel
                                </Button>
                                <Button variant="success" onClick={handleSave}>
                                    Save
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
        </>
    );
};

export default NumTranslationAddEdit;
